import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DownloadRounded, RefreshRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import './CallRecordingAudio.css';

export default function CallRecordingAudio({ correlationId }) {
    const { apiUrl } = useContext(AuthContext);
    const { t } = useTranslation();
    const pending = useRef(null);
    const [status, setStatus] = useState('idle');
    const [recordings, setRecordings] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => () => pending.current?.abort(), []);

    const loadRecordings = async () => {
        if (pending.current || !correlationId) return;
        const controller = new AbortController();
        pending.current = controller;
        setStatus('loading');
        setError('');
        setRecordings([]);
        try {
            const response = await axios.get(
                `${apiUrl}webex/calling/recordings/${encodeURIComponent(correlationId)}`,
                { withCredentials: true, signal: controller.signal, timeout: 60000 }
            );
            if (!Array.isArray(response.data?.recordings)) throw new Error('Invalid recordings response');
            if (controller.signal.aborted) return;
            setRecordings(response.data.recordings);
            setStatus('loaded');
        } catch (requestError) {
            if (controller.signal.aborted) return;
            const httpStatus = requestError.response?.status;
            setError(httpStatus === 401 || httpStatus === 403
                ? t('callAudio.accessDenied', { defaultValue: 'Accès refusé. Vérifiez votre connexion.' })
                : httpStatus === 404
                    ? t('callAudio.notFound', { defaultValue: 'Appel introuvable.' })
                    : t('callAudio.loadError', { defaultValue: 'Impossible de charger les enregistrements.' }));
            setStatus('error');
        } finally {
            if (pending.current === controller) pending.current = null;
        }
    };

    return (
        <div className="call-recording-audio" aria-busy={status === 'loading'}>
            <button
                type="button"
                className={status === 'loaded' ? 'call-recording-audio-refresh' : undefined}
                title={status === 'loaded' ? t('callAudio.refresh') : undefined}
                aria-label={status === 'loaded' ? t('callAudio.refresh') : undefined}
                disabled={!correlationId || status === 'loading'}
                onClick={loadRecordings}
            >
                {status === 'loading'
                    ? t('callAudio.loading', { defaultValue: 'Chargement…' })
                    : status === 'idle'
                        ? t('callAudio.listen', { defaultValue: 'Écouter' })
                        : status === 'error'
                            ? t('callAudio.retry', { defaultValue: 'Réessayer' })
                            : <RefreshRounded fontSize="inherit" />}
            </button>
            {error && <span role="alert" className="call-recording-audio-error">{error}</span>}
            {status === 'loaded' && recordings.length === 0 && (
                <span role="status">{t('callAudio.empty', { defaultValue: 'Pas d’audio pour cet appel' })}</span>
            )}
            {recordings.map((recording, index) => (
                <div key={recording.id} className="call-recording-audio-item">
                    {recordings.length > 1 && (
                        <span>{t('callAudio.recording', { defaultValue: 'Enregistrement {{number}}', number: index + 1 })}
                            {recording.ownerEmail ? ` · ${recording.ownerEmail}` : ''}
                        </span>
                    )}
                    {recording.audioDownloadLink ? (
                        <div className="call-recording-audio-controls">
                        <audio
                            key={recording.audioDownloadLink}
                            controls
                            preload="none"
                            src={recording.audioDownloadLink}
                            aria-label={t('callAudio.recording', { defaultValue: 'Enregistrement {{number}}', number: index + 1 })}
                            onPlay={(event) => {
                                if (recording.expiration && Date.parse(recording.expiration) <= Date.now()) {
                                    event.currentTarget.pause();
                                    setError(t('callAudio.expired', { defaultValue: 'Lien expiré. Actualisez les liens pour écouter.' }));
                                }
                            }}
                            onError={() => setError(t('callAudio.playbackError', { defaultValue: 'Lecture impossible. Actualisez les liens puis réessayez.' }))}
                        />
                        <a
                            className="call-recording-audio-download"
                            href={recording.audioDownloadLink}
                            download={`webex-${recording.id}.mp3`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t('callAudio.download', { defaultValue: 'Télécharger le MP3' })}
                            aria-label={t('callAudio.download', { defaultValue: 'Télécharger le MP3' })}
                            onClick={(event) => {
                                if (recording.expiration && Date.parse(recording.expiration) <= Date.now()) {
                                    event.preventDefault();
                                    setError(t('callAudio.expired'));
                                }
                            }}
                        >
                            <DownloadRounded fontSize="inherit" />
                        </a>
                        </div>
                    ) : (
                        <span>{t('callAudio.unavailable', { defaultValue: 'Audio indisponible pour cet enregistrement.' })}</span>
                    )}
                </div>
            ))}
        </div>
    );
}
